// src/components/admin/AssetManagement.js
import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useSearchParams } from 'react-router-dom';
import assetService from '../../services/asset.service';
import employeeService from '../../services/employee.service';
import {
    BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer
} from 'recharts';
import {
    Package, Plus, Search, X, CheckCircle, AlertCircle, Loader2,
    Edit3, Trash2, UserPlus, RotateCcw, Wrench,
    DollarSign, Clock, Tag, LayoutDashboard, TrendingUp,
    ArrowRight, Activity, ChevronRight, Shield, Users, CalendarClock
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────
// Transform snake_case DB row to camelCase for frontend
const toCamel = (row) => {
    if (!row || typeof row !== 'object') return row;
    const out = {};
    for (const [k, v] of Object.entries(row)) {
        const ck = k.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
        out[ck] = v;
    }
    return out;
};
const toCamelList = (rows) => (rows || []).map(toCamel);

const STATUS_COLORS = {
    available: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    assigned: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    maintenance: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    retired: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    lost: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const CONDITION_COLORS = {
    new: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    good: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    fair: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    poor: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const TABS = [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { key: 'assets', label: 'Assets', icon: Package },
    { key: 'categories', label: 'Categories', icon: Tag },
    { key: 'maintenance', label: 'Maintenance', icon: Wrench },
    { key: 'audit', label: 'Audit Log', icon: Clock },
];

// ─── Main Component ──────────────────────────────────────────────────
const AssetManagement = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') || 'dashboard';

    const [stats, setStats] = useState(null);
    const [assets, setAssets] = useState([]);
    const [categories, setCategories] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [totalAssets, setTotalAssets] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [maintenanceRecords, setMaintenanceRecords] = useState([]);
    const [totalMaintenance, setTotalMaintenance] = useState(0);
    const [maintPage, setMaintPage] = useState(1);
    const [maintStatusFilter, setMaintStatusFilter] = useState('');
    const [maintTypeFilter, setMaintTypeFilter] = useState('');
    const [auditRecords, setAuditRecords] = useState([]);
    const [totalAudit, setTotalAudit] = useState(0);
    const [auditPage, setAuditPage] = useState(1);
    const [auditActionFilter, setAuditActionFilter] = useState('');

    // Modals
    const [showAssetModal, setShowAssetModal] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [error, setError] = useState(null);

    // Load categories + employees on mount
    useEffect(() => {
        loadCategories();
        loadEmployees();
    }, []);

    useEffect(() => {
        loadData();
    }, [activeTab, page, search, statusFilter, categoryFilter, maintPage, maintStatusFilter, maintTypeFilter, auditPage, auditActionFilter]);

    const loadCategories = async () => {
        try {
            const res = await assetService.getCategories();
            if (res.success) setCategories(toCamelList(res.data));
        } catch (err) { console.error('Error loading categories:', err); }
    };

    const loadEmployees = async () => {
        try {
            const emps = await employeeService.getAllEmployees();
            setEmployees(emps || []);
        } catch (err) { console.error('Error loading employees:', err); }
    };

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            if (activeTab === 'dashboard') {
                const res = await assetService.getStats();
                if (res.success) {
                    const d = res.data;
                    // Transform all nested arrays
                    setStats({
                        ...toCamel(d),
                        recentAssignments: toCamelList(d.recentAssignments),
                        upcomingMaintenance: toCamelList(d.upcomingMaintenance),
                        conditionBreakdown: toCamelList(d.conditionBreakdown),
                        categoryDistribution: toCamelList(d.categoryDistribution),
                        maintenanceByType: toCamelList(d.maintenanceByType),
                        maintenanceTrend: toCamelList(d.maintenanceTrend),
                        topMaintAssets: toCamelList(d.topMaintAssets),
                        agingAssets: toCamelList(d.agingAssets),
                        maintenanceStats: d.maintenanceStats ? toCamel(d.maintenanceStats) : null,
                    });
                } else setError('Failed to load dashboard stats');
            } else if (activeTab === 'assets') {
                const params = { page, limit: 25 };
                if (search) params.search = search;
                if (statusFilter) params.status = statusFilter;
                if (categoryFilter) params.category_id = categoryFilter;
                const res = await assetService.getAssets(params);
                if (res.success) {
                    setAssets(toCamelList(res.data));
                    setTotalAssets(res.total);
                } else setError('Failed to load assets');
            } else if (activeTab === 'categories') {
                const res = await assetService.getCategories();
                if (res.success) setCategories(toCamelList(res.data));
                else setError('Failed to load categories');
            } else if (activeTab === 'maintenance') {
                const params = { page: maintPage, limit: 25 };
                if (maintStatusFilter) params.status = maintStatusFilter;
                if (maintTypeFilter) params.type = maintTypeFilter;
                const res = await assetService.getAllMaintenance(params);
                if (res.success) {
                    setMaintenanceRecords(toCamelList(res.data));
                    setTotalMaintenance(res.total);
                } else setError('Failed to load maintenance records');
            } else if (activeTab === 'audit') {
                const params = { page: auditPage, limit: 25 };
                if (auditActionFilter) params.action = auditActionFilter;
                const res = await assetService.getAllAuditLog(params);
                if (res.success) {
                    setAuditRecords(toCamelList(res.data));
                    setTotalAudit(res.total);
                } else setError('Failed to load audit log');
            }
        } catch (err) {
            console.error('Error loading data:', err);
            setError(err.response?.data?.error || err.message || 'Failed to load data.');
        } finally { setLoading(false); }
    };

    const handleTabChange = (tab, opts = {}) => {
        setSearchParams({ tab });
        setPage(1); setSearch(''); setStatusFilter(opts.status || '');
        setCategoryFilter(opts.category || '');
        setMaintPage(1); setMaintStatusFilter(''); setMaintTypeFilter('');
        setAuditPage(1); setAuditActionFilter('');
    };

    // ─── Dashboard Tab ───────────────────────────────────────────────
    const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];

    const DashboardTab = () => {
        if (loading) return <LoadingSpinner />;
        if (error) return <ErrorCard error={error} onRetry={loadData} isDark={isDark} />;

        const total = parseInt(stats?.total) || 0;

        // Improved empty state for new users
        if (!stats || total === 0) {
            return (
                <div className={`rounded-xl p-12 text-center border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className={`w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center ${isDark ? 'bg-blue-900/30' : 'bg-blue-50'}`}>
                        <Package className={`w-10 h-10 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
                    </div>
                    <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Start tracking your assets</h3>
                    <p className={`text-sm max-w-md mx-auto mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Add laptops, monitors, phones, and other company equipment to track assignments, maintenance, and depreciation in one place.
                    </p>
                    <div className="flex items-center justify-center gap-3">
                        <button onClick={() => { setEditingItem(null); setShowAssetModal(true); }}
                            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                            <Plus className="w-4 h-4" /> Add Your First Asset
                        </button>
                        <button onClick={() => { setEditingItem(null); setShowCategoryModal(true); }}
                            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg border transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                            <Tag className="w-4 h-4" /> Set Up Categories
                        </button>
                    </div>
                </div>
            );
        }

        const purchaseCost = Number(stats.totalPurchaseValue || 0);
        const currentValue = Number(stats.totalCurrentValue || 0);
        const depreciation = Number(stats.totalDepreciation || 0);
        const maintSpend = Number(stats.maintenanceStats?.completedCost || 0);
        const fmt = (n) => `₹${n.toLocaleString('en-IN')}`;

        const statCards = [
            { label: 'Total Assets', value: total, color: 'from-blue-500 to-blue-600', icon: Package, filter: {} },
            { label: 'Assigned', value: parseInt(stats.assigned) || 0, color: 'from-indigo-500 to-blue-600', icon: Users, filter: { status: 'assigned' } },
            { label: 'Available', value: parseInt(stats.available) || 0, color: 'from-green-500 to-emerald-600', icon: CheckCircle, filter: { status: 'available' } },
            { label: 'Maintenance', value: parseInt(stats.maintenance) || 0, color: 'from-amber-500 to-orange-600', icon: Wrench, filter: { status: 'maintenance' } },
            { label: 'Retired', value: parseInt(stats.retired) || 0, color: 'from-gray-500 to-gray-600', icon: Shield, filter: { status: 'retired' } },
        ];

        const statusData = [
            { name: 'Available', value: parseInt(stats.available) || 0 },
            { name: 'Assigned', value: parseInt(stats.assigned) || 0 },
            { name: 'Maintenance', value: parseInt(stats.maintenance) || 0 },
            { name: 'Retired', value: parseInt(stats.retired) || 0 },
        ].filter(d => d.value > 0);

        const conditionData = (stats.conditionBreakdown || []).map(c => ({
            name: (c.condition || 'Unknown').charAt(0).toUpperCase() + (c.condition || 'Unknown').slice(1),
            value: parseInt(c.count) || 0,
        }));

        const categoryData = (stats.categoryDistribution || []).map(c => ({
            name: c.category,
            count: parseInt(c.count) || 0,
            value: Number(c.totalValue || 0),
        }));

        const maintTrend = (stats.maintenanceTrend || []).map(t => ({
            month: t.month ? new Date(t.month + '-01').toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }) : '',
            count: parseInt(t.count) || 0,
            cost: Number(t.cost || 0),
        }));

        const maintTypeData = (stats.maintenanceByType || []).map(t => ({
            name: (t.type || 'Other').charAt(0).toUpperCase() + (t.type || 'Other').slice(1),
            value: parseInt(t.count) || 0,
            cost: Number(t.totalCost || 0),
        }));

        const utilization = stats.utilization || 0;
        const maintCompletionRate = stats.maintCompletionRate || 0;

        // Build unified activity feed from recent assignments
        const activityFeed = [
            ...(stats.recentAssignments || []).map(a => ({
                type: 'assigned',
                icon: UserPlus,
                iconBg: 'bg-blue-100 dark:bg-blue-900/30',
                iconColor: 'text-blue-600 dark:text-blue-400',
                title: a.assetName || a.asset_name,
                sub: `Assigned to ${a.employeeName || a.employee_name}`,
                code: a.assetCode || a.asset_code,
                date: new Date(a.assignedAt || a.assigned_at),
            })),
        ].sort((a, b) => b.date - a.date).slice(0, 8);

        const RingGauge = ({ value, label, color, size = 96 }) => {
            const r = (size - 12) / 2;
            const circ = 2 * Math.PI * r;
            const offset = circ - (value / 100) * circ;
            return (
                <div className="flex flex-col items-center min-w-[110px]">
                    <div className="relative">
                        <svg width={size} height={size} className="-rotate-90">
                            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={isDark ? '#374151' : '#E5E7EB'} strokeWidth="7" />
                            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="7"
                                strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <span className={`text-base font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}%</span>
                        </div>
                    </div>
                    <span className={`text-xs mt-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</span>
                </div>
            );
        };

        return (
            <div className="space-y-6">
                {/* Row 1: Clickable Stat Cards */}
                <div className="tour-asset-stats grid grid-cols-2 md:grid-cols-5 gap-4">
                    {statCards.map((c, i) => (
                        <button key={i} onClick={() => handleTabChange('assets', c.filter)}
                            className={`rounded-xl p-4 bg-gradient-to-br ${c.color} text-white text-left transition-all hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]`}>
                            <div className="flex items-center justify-between mb-2">
                                <c.icon className="w-5 h-5 opacity-80" />
                                <ArrowRight className="w-3.5 h-3.5 opacity-50" />
                            </div>
                            <div className="text-2xl font-bold">{c.value}</div>
                            <div className="text-xs opacity-80">{c.label}</div>
                        </button>
                    ))}
                </div>

                {/* Row 2: Quick Actions */}
                <div className={`rounded-xl border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Quick Actions</h3>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <button onClick={() => { setEditingItem(null); setShowAssetModal(true); }}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                            <Plus className="w-4 h-4" /> Add Asset
                        </button>
                        <button onClick={() => { setEditingItem(null); setShowCategoryModal(true); }}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                            <Tag className="w-4 h-4" /> Add Category
                        </button>
                    </div>
                </div>

                {/* Row 3: Value Summary + Gauges + Status Distribution */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Financial Summary */}
                    <div className={`rounded-xl p-5 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                        <h3 className={`text-sm font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Asset Value</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Purchase Cost</span>
                                <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{fmt(purchaseCost)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Current Value</span>
                                <span className="text-sm font-semibold text-green-600 dark:text-green-400">{fmt(currentValue)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Depreciation</span>
                                <span className="text-sm font-semibold text-red-500 dark:text-red-400">-{fmt(depreciation)}</span>
                            </div>
                            <div className={`pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                                <div className="flex justify-between items-center">
                                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Maintenance Spend</span>
                                    <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{fmt(maintSpend)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-around gap-4 mt-5 pt-4 border-t dark:border-gray-700">
                            <RingGauge value={utilization} label="Utilization" color="#3B82F6" />
                            <RingGauge value={maintCompletionRate} label="Maint. Done" color="#10B981" />
                        </div>
                    </div>

                    {/* Status Distribution Pie */}
                    {statusData.length > 0 && (
                        <div className={`rounded-xl p-5 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                            <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Status Distribution</h3>
                            <ResponsiveContainer width="100%" height={180}>
                                <PieChart>
                                    <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} innerRadius={35} paddingAngle={2}>
                                        {statusData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
                                    </Pie>
                                    <Tooltip formatter={(v) => `${v} assets`} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {statusData.map((d, i) => (
                                    <button key={i} onClick={() => handleTabChange('assets', { status: d.name.toLowerCase() })}
                                        className="flex items-center gap-1 text-[10px] hover:underline cursor-pointer">
                                        <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i] }} />
                                        <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{d.name} ({d.value})</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Condition Breakdown Pie */}
                    {conditionData.length > 0 && (
                        <div className={`rounded-xl p-5 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                            <h3 className={`text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Asset Condition</h3>
                            <ResponsiveContainer width="100%" height={180}>
                                <PieChart>
                                    <Pie data={conditionData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} innerRadius={35} paddingAngle={2}>
                                        {conditionData.map((_, i) => <Cell key={i} fill={['#10B981', '#3B82F6', '#F59E0B', '#EF4444'][i] || CHART_COLORS[i]} />)}
                                    </Pie>
                                    <Tooltip formatter={(v) => `${v} assets`} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {conditionData.map((d, i) => (
                                    <span key={i} className="flex items-center gap-1 text-[10px]">
                                        <span className="w-2 h-2 rounded-full" style={{ background: ['#10B981', '#3B82F6', '#F59E0B', '#EF4444'][i] }} />
                                        <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{d.name} ({d.value})</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Row 4: Recent Activity + Assets by Category */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Recent Activity Timeline */}
                    <div className={`rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                        <div className={`flex items-center justify-between px-5 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Recent Activity</h3>
                            <button onClick={() => handleTabChange('audit')}
                                className={`text-xs flex items-center gap-1 ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}>
                                View all <ChevronRight className="w-3 h-3" />
                            </button>
                        </div>
                        {activityFeed.length === 0 ? (
                            <div className="py-8 text-center">
                                <Activity className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No recent activity</p>
                            </div>
                        ) : (
                            <div className="divide-y dark:divide-gray-700">
                                {activityFeed.map((a, i) => (
                                    <div key={i} className="flex items-center gap-3 px-5 py-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${a.iconBg}`}>
                                            <a.icon className={`w-4 h-4 ${a.iconColor}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{a.title}</p>
                                            <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{a.sub}</p>
                                        </div>
                                        <span className={`text-[10px] flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                            {a.date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Assets by Category */}
                    {categoryData.length > 0 ? (
                        <div className={`rounded-xl p-5 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                            <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Assets by Category</h3>
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={categoryData} layout="vertical" margin={{ left: 10 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#E5E7EB'} horizontal={false} />
                                    <XAxis type="number" tick={{ fontSize: 11, fill: isDark ? '#9CA3AF' : '#6B7280' }} />
                                    <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11, fill: isDark ? '#9CA3AF' : '#6B7280' }} />
                                    <Tooltip formatter={(v, name) => name === 'count' ? `${v} assets` : `₹${Number(v).toLocaleString('en-IN')}`} />
                                    <Bar dataKey="count" fill="#3B82F6" radius={[0,4,4,0]} barSize={20} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : (
                        <div className={`rounded-xl p-5 border flex flex-col items-center justify-center ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                            <Tag className={`w-8 h-8 mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No categories configured yet</p>
                            <button onClick={() => { setEditingItem(null); setShowCategoryModal(true); }}
                                className="mt-3 text-xs text-blue-600 dark:text-blue-400 hover:underline">Add a category</button>
                        </div>
                    )}
                </div>

                {/* Row 5: Upcoming Maintenance + Aging Assets + Top Maintenance Cost */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Upcoming Maintenance */}
                    <Card title="Upcoming Maintenance" isDark={isDark} action={stats.upcomingMaintenance?.length > 0 ? { label: 'View all', onClick: () => handleTabChange('maintenance') } : null}>
                        {(stats.upcomingMaintenance || []).length === 0 ? (
                            <div className="py-6 text-center">
                                <CalendarClock className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No upcoming maintenance</p>
                            </div>
                        ) : stats.upcomingMaintenance.map((m, i) => (
                            <div key={i} className={`flex items-center justify-between px-4 py-3 ${i > 0 ? `border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}` : ''}`}>
                                <div className="min-w-0 flex-1">
                                    <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{m.assetName || m.asset_name}</p>
                                    <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{m.description}</p>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ml-2 ${m.status === 'scheduled' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}`}>
                                    {m.scheduledDate || m.scheduled_date ? new Date(m.scheduledDate || m.scheduled_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'No date'}
                                </span>
                            </div>
                        ))}
                    </Card>

                    {/* Aging Assets */}
                    <Card title="Aging Assets (3+ yrs)" isDark={isDark}>
                        {(stats.agingAssets || []).length === 0 ? (
                            <div className="py-6 text-center">
                                <Shield className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No aging assets</p>
                            </div>
                        ) : stats.agingAssets.map((a, i) => (
                            <div key={i} className={`flex items-center justify-between px-4 py-3 ${i > 0 ? `border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}` : ''}`}>
                                <div>
                                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{a.name}</p>
                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{a.assetCode || a.asset_code}</p>
                                </div>
                                <div className="text-right">
                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{new Date(a.purchaseDate || a.purchase_date).getFullYear()}</p>
                                    <p className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>₹{Number(a.purchaseCost || 0).toLocaleString('en-IN')}</p>
                                </div>
                            </div>
                        ))}
                    </Card>

                    {/* Top Assets by Maintenance Cost */}
                    <Card title="Top Maintenance Cost" isDark={isDark}>
                        {(stats.topMaintAssets || []).length === 0 ? (
                            <div className="py-6 text-center">
                                <DollarSign className={`w-8 h-8 mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No maintenance data</p>
                            </div>
                        ) : stats.topMaintAssets.map((a, i) => (
                            <div key={i} className={`flex items-center justify-between px-4 py-3 ${i > 0 ? `border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}` : ''}`}>
                                <div className="flex items-center gap-2">
                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' : isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>{i + 1}</span>
                                    <div>
                                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{a.name}</p>
                                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{a.maintenanceCount} record{a.maintenanceCount !== 1 ? 's' : ''}</p>
                                    </div>
                                </div>
                                <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>₹{Number(a.totalMaintenanceCost || 0).toLocaleString('en-IN')}</span>
                            </div>
                        ))}
                    </Card>
                </div>

                {/* Row 6: Maintenance Trend + Maintenance by Type (analytics) */}
                {(maintTrend.length > 0 || maintTypeData.length > 0) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {maintTrend.length > 0 && (
                            <div className={`rounded-xl p-5 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                                <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Maintenance Trend (6mo)</h3>
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={maintTrend}>
                                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#E5E7EB'} />
                                        <XAxis dataKey="month" tick={{ fontSize: 11, fill: isDark ? '#9CA3AF' : '#6B7280' }} />
                                        <YAxis yAxisId="left" tick={{ fontSize: 11, fill: isDark ? '#9CA3AF' : '#6B7280' }} />
                                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: isDark ? '#9CA3AF' : '#6B7280' }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                                        <Tooltip />
                                        <Legend />
                                        <Bar yAxisId="left" dataKey="count" fill="#8B5CF6" name="Records" radius={[4,4,0,0]} />
                                        <Bar yAxisId="right" dataKey="cost" fill="#F59E0B" name="Cost (₹)" radius={[4,4,0,0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                        {maintTypeData.length > 0 && (
                            <div className={`rounded-xl p-5 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                                <h3 className={`text-sm font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Maintenance by Type</h3>
                                <ResponsiveContainer width="100%" height={180}>
                                    <PieChart>
                                        <Pie data={maintTypeData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65} innerRadius={35} paddingAngle={2}>
                                            {maintTypeData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                        </Pie>
                                        <Tooltip formatter={(v, n, p) => `${v} records (₹${Number(p.payload.cost).toLocaleString('en-IN')})`} />
                                    </PieChart>
                                </ResponsiveContainer>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {maintTypeData.map((d, i) => (
                                        <span key={i} className="flex items-center gap-1 text-[10px]">
                                            <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                                            <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{d.name} ({d.value})</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // ─── Assets Tab ──────────────────────────────────────────────────
    const AssetsTab = () => (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg flex-1 min-w-[200px] ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                    <Search className="w-4 h-4 text-gray-400" />
                    <input type="text" placeholder="Search assets..." value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        className={`flex-1 bg-transparent text-sm outline-none ${isDark ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`} />
                </div>
                <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                    className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-800 border border-gray-700 text-white' : 'bg-white border border-gray-200 text-gray-900'}`}>
                    <option value="">All Status</option>
                    <option value="available">Available</option>
                    <option value="assigned">Assigned</option>
                    <option value="maintenance">Maintenance</option>
                    <option value="retired">Retired</option>
                </select>
                <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
                    className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-800 border border-gray-700 text-white' : 'bg-white border border-gray-200 text-gray-900'}`}>
                    <option value="">All Categories</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button onClick={() => { setEditingItem(null); setShowAssetModal(true); }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                    <Plus className="w-4 h-4" /> Add Asset
                </button>
            </div>

            {loading ? <LoadingSpinner /> : assets.length === 0 ? (
                <EmptyState message="No assets found" />
            ) : (
                <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                                    {['Code', 'Name', 'Category', 'Status', 'Assigned To', 'Value', 'Actions'].map(h => (
                                        <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {assets.map((a, i) => (
                                    <tr key={a.id} className={`cursor-pointer transition-colors ${isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} ${i > 0 ? `border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}` : ''}`}
                                        onClick={() => { setSelectedAsset(a); setShowDetailModal(true); }}>
                                        <td className={`px-4 py-3 text-sm font-mono font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{a.assetCode || '—'}</td>
                                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{a.name}</td>
                                        <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{a.categoryName || '—'}</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[a.status] || ''}`}>{a.status}</span>
                                        </td>
                                        <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{a.assignedEmployeeName || '—'}</td>
                                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>₹{Number(a.currentValue || a.current_value || 0).toLocaleString('en-IN')}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                {a.status === 'available' && (
                                                    <button onClick={() => { setSelectedAsset(a); setShowAssignModal(true); }}
                                                        className="p-1.5 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400" title="Assign">
                                                        <UserPlus className="w-4 h-4" />
                                                    </button>
                                                )}
                                                {a.status === 'assigned' && (
                                                    <button onClick={async () => {
                                                        try { await assetService.returnAsset(a.id, { returnCondition: 'good' }); loadData(); }
                                                        catch (err) { alert(err.response?.data?.error || 'Failed to return asset'); }
                                                    }} className="p-1.5 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400" title="Return">
                                                        <RotateCcw className="w-4 h-4" />
                                                    </button>
                                                )}
                                                <button onClick={() => { setSelectedAsset(a); setShowMaintenanceModal(true); }}
                                                    className="p-1.5 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400" title="Maintenance">
                                                    <Wrench className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => { setEditingItem(a); setShowAssetModal(true); }}
                                                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-500" title="Edit">
                                                    <Edit3 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {totalAssets > 25 && (
                        <div className={`flex items-center justify-between px-4 py-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Showing {((page - 1) * 25) + 1}–{Math.min(page * 25, totalAssets)} of {totalAssets}
                            </span>
                            <div className="flex gap-2">
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                    className={`px-3 py-1 rounded text-sm ${isDark ? 'bg-gray-700 text-gray-300 disabled:opacity-50' : 'bg-gray-100 text-gray-700 disabled:opacity-50'}`}>Prev</button>
                                <button onClick={() => setPage(p => p + 1)} disabled={page * 25 >= totalAssets}
                                    className={`px-3 py-1 rounded text-sm ${isDark ? 'bg-gray-700 text-gray-300 disabled:opacity-50' : 'bg-gray-100 text-gray-700 disabled:opacity-50'}`}>Next</button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    // ─── Categories Tab ──────────────────────────────────────────────
    const CategoriesTab = () => (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Asset Categories</h3>
                <button onClick={() => { setEditingItem(null); setShowCategoryModal(true); }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                    <Plus className="w-4 h-4" /> Add Category
                </button>
            </div>
            {loading ? <LoadingSpinner /> : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {categories.map(c => (
                        <div key={c.id} className={`rounded-xl p-4 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{c.name}</h4>
                                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{c.description || 'No description'}</p>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => { setEditingItem(c); setShowCategoryModal(true); }} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                                        <Edit3 className="w-3.5 h-3.5 text-gray-500" />
                                    </button>
                                    <button onClick={async () => { await assetService.deleteCategory(c.id); loadCategories(); loadData(); }}
                                        className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20">
                                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                    </button>
                                </div>
                            </div>
                            <div className="flex gap-4 mt-3">
                                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    <span className="font-medium">{c.depreciationRate || c.depreciation_rate || 15}%</span> depreciation/yr
                                </div>
                                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    <span className="font-medium">{c.usefulLifeYears || c.useful_life_years || 5}yr</span> useful life
                                </div>
                                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    <span className="font-medium">{c.assetCount || c.asset_count || 0}</span> {Number(c.assetCount || c.asset_count || 0) === 1 ? 'asset' : 'assets'}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // ─── Maintenance Tab ─────────────────────────────────────────────
    const MAINT_STATUS_COLORS = {
        scheduled: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    };
    const MAINT_TYPE_ICONS = { repair: Wrench, inspection: CheckCircle, upgrade: Package, calibration: Clock };

    const MaintenanceTab = () => {
        const handleUpdateStatus = async (record, newStatus) => {
            try {
                await assetService.updateMaintenance(record.assetId, record.id, { status: newStatus });
                loadData();
            } catch (err) {
                alert(err.response?.data?.error || 'Failed to update maintenance status');
            }
        };

        return (
            <div className="space-y-4">
                <div className="flex flex-wrap gap-3 items-center">
                    <select value={maintStatusFilter} onChange={(e) => { setMaintStatusFilter(e.target.value); setMaintPage(1); }}
                        className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-800 border border-gray-700 text-white' : 'bg-white border border-gray-200 text-gray-900'}`}>
                        <option value="">All Status</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                    </select>
                    <select value={maintTypeFilter} onChange={(e) => { setMaintTypeFilter(e.target.value); setMaintPage(1); }}
                        className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-800 border border-gray-700 text-white' : 'bg-white border border-gray-200 text-gray-900'}`}>
                        <option value="">All Types</option>
                        <option value="repair">Repair</option>
                        <option value="inspection">Inspection</option>
                        <option value="upgrade">Upgrade</option>
                        <option value="calibration">Calibration</option>
                    </select>
                    <span className={`text-sm ml-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {totalMaintenance} record{totalMaintenance !== 1 ? 's' : ''}
                    </span>
                </div>

                {loading ? <LoadingSpinner /> : maintenanceRecords.length === 0 ? (
                    <EmptyState message="No maintenance records found" />
                ) : (
                    <>
                        <div className="space-y-3">
                            {maintenanceRecords.map((m) => {
                                const TypeIcon = MAINT_TYPE_ICONS[m.type] || Wrench;
                                return (
                                    <div key={m.id} className={`rounded-xl p-4 border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex items-start gap-3">
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                                                    <TypeIcon className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{m.assetName || m.asset_name}</span>
                                                        <span className={`text-xs font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{m.assetCode || m.asset_code}</span>
                                                    </div>
                                                    <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{m.description}</p>
                                                    <div className={`flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        <span className="capitalize">{m.type}</span>
                                                        {m.scheduledDate && <span>Scheduled: {new Date(m.scheduledDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                                                        {m.completedDate && <span>Completed: {new Date(m.completedDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                                                        {m.cost > 0 && <span>Cost: ₹{Number(m.cost).toLocaleString('en-IN')}</span>}
                                                        {m.vendor && <span>Vendor: {m.vendor}</span>}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${MAINT_STATUS_COLORS[m.status] || ''}`}>{(m.status || '').replace('_', ' ')}</span>
                                                {m.status === 'scheduled' && (
                                                    <button onClick={() => handleUpdateStatus(m, 'in_progress')}
                                                        className="px-3 py-1 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700">Start</button>
                                                )}
                                                {(m.status === 'scheduled' || m.status === 'in_progress') && (
                                                    <button onClick={() => handleUpdateStatus(m, 'completed')}
                                                        className="px-3 py-1 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700">Complete</button>
                                                )}
                                                {(m.status === 'scheduled' || m.status === 'in_progress') && (
                                                    <button onClick={() => handleUpdateStatus(m, 'cancelled')}
                                                        className={`px-3 py-1 text-xs font-medium rounded-lg ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>Cancel</button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {totalMaintenance > 25 && (
                            <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    Showing {((maintPage - 1) * 25) + 1}–{Math.min(maintPage * 25, totalMaintenance)} of {totalMaintenance}
                                </span>
                                <div className="flex gap-2">
                                    <button onClick={() => setMaintPage(p => Math.max(1, p - 1))} disabled={maintPage === 1}
                                        className={`px-3 py-1 rounded text-sm ${isDark ? 'bg-gray-700 text-gray-300 disabled:opacity-50' : 'bg-gray-100 text-gray-700 disabled:opacity-50'}`}>Prev</button>
                                    <button onClick={() => setMaintPage(p => p + 1)} disabled={maintPage * 25 >= totalMaintenance}
                                        className={`px-3 py-1 rounded text-sm ${isDark ? 'bg-gray-700 text-gray-300 disabled:opacity-50' : 'bg-gray-100 text-gray-700 disabled:opacity-50'}`}>Next</button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    // ─── Audit Log Tab ───────────────────────────────────────────────
    const AUDIT_ACTION_COLORS = {
        created: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
        updated: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
        assigned: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
        returned: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
        maintenance_scheduled: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
        maintenance_completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
        status_changed: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
        retired: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
        deleted: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    };
    const AUDIT_ACTION_LABELS = {
        created: 'Created', updated: 'Updated', assigned: 'Assigned', returned: 'Returned',
        maintenance_scheduled: 'Maintenance Scheduled', maintenance_completed: 'Maintenance Completed',
        status_changed: 'Status Changed', retired: 'Retired', deleted: 'Deleted',
    };

    const AuditTab = () => (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
                <select value={auditActionFilter} onChange={(e) => { setAuditActionFilter(e.target.value); setAuditPage(1); }}
                    className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-800 border border-gray-700 text-white' : 'bg-white border border-gray-200 text-gray-900'}`}>
                    <option value="">All Actions</option>
                    <option value="created">Created</option>
                    <option value="updated">Updated</option>
                    <option value="assigned">Assigned</option>
                    <option value="returned">Returned</option>
                    <option value="maintenance_scheduled">Maintenance Scheduled</option>
                    <option value="maintenance_completed">Maintenance Completed</option>
                    <option value="status_changed">Status Changed</option>
                    <option value="retired">Retired</option>
                    <option value="deleted">Deleted</option>
                </select>
                <span className={`text-sm ml-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {totalAudit} entr{totalAudit !== 1 ? 'ies' : 'y'}
                </span>
            </div>

            {loading ? <LoadingSpinner /> : auditRecords.length === 0 ? (
                <EmptyState message="No audit log entries found" />
            ) : (
                <>
                    <div className="space-y-2">
                        {auditRecords.map((a) => (
                            <div key={a.id} className={`flex items-start gap-3 p-3 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${AUDIT_ACTION_COLORS[a.action] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                                    {(a.action || '?')[0].toUpperCase()}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{a.assetName || a.asset_name || 'Unknown Asset'}</span>
                                        <span className={`text-xs font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{a.assetCode || a.asset_code}</span>
                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${AUDIT_ACTION_COLORS[a.action] || ''}`}>
                                            {AUDIT_ACTION_LABELS[a.action] || a.action}
                                        </span>
                                    </div>
                                    <div className={`flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {a.performedByName && <span>By {a.performedByName}</span>}
                                        <span>{new Date(a.createdAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                    {(a.oldValue || a.newValue) && (
                                        <div className={`mt-2 text-xs space-y-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                            {a.newValue && (
                                                <div className="mt-1">
                                                    {typeof a.newValue === 'string' ? (
                                                        <p className="truncate">→ {a.newValue}</p>
                                                    ) : (
                                                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                                                            {Object.entries(a.newValue)
                                                                .filter(([k]) => !['id', 'company_id', 'created_at', 'updated_at', 'created_by'].includes(k))
                                                                .map(([k, v]) => v != null && v !== '' ? (
                                                                    <span key={k} className={`${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                        <span className="font-medium capitalize">{k.replace(/_/g, ' ')}</span>: {String(v)}
                                                                    </span>
                                                                ) : null)}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    {totalAudit > 25 && (
                        <div className={`flex items-center justify-between px-4 py-3 rounded-xl border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Showing {((auditPage - 1) * 25) + 1}–{Math.min(auditPage * 25, totalAudit)} of {totalAudit}
                            </span>
                            <div className="flex gap-2">
                                <button onClick={() => setAuditPage(p => Math.max(1, p - 1))} disabled={auditPage === 1}
                                    className={`px-3 py-1 rounded text-sm ${isDark ? 'bg-gray-700 text-gray-300 disabled:opacity-50' : 'bg-gray-100 text-gray-700 disabled:opacity-50'}`}>Prev</button>
                                <button onClick={() => setAuditPage(p => p + 1)} disabled={auditPage * 25 >= totalAudit}
                                    className={`px-3 py-1 rounded text-sm ${isDark ? 'bg-gray-700 text-gray-300 disabled:opacity-50' : 'bg-gray-100 text-gray-700 disabled:opacity-50'}`}>Next</button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Page Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Asset Management</h2>
                    <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Track, assign, and maintain company assets
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800 shadow-gray-900' : 'bg-white'}`}>
                <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <nav className="tour-asset-tabs flex space-x-8 px-6">
                        {TABS.map(tab => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.key;
                            return (
                                <button key={tab.key} onClick={() => handleTabChange(tab.key)}
                                    className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                                        isActive
                                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}>
                                    <Icon className="w-4 h-4" />{tab.label}
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>

            {/* Content */}
            {activeTab === 'dashboard' && <DashboardTab />}
            {activeTab === 'assets' && <AssetsTab />}
            {activeTab === 'categories' && <CategoriesTab />}
            {activeTab === 'maintenance' && <MaintenanceTab />}
            {activeTab === 'audit' && <AuditTab />}

            {/* Modals */}
            {showAssetModal && (
                <AssetFormModal
                    isDark={isDark} asset={editingItem} categories={categories}
                    onClose={() => { setShowAssetModal(false); setEditingItem(null); }}
                    onSave={async (data) => {
                        if (editingItem) await assetService.updateAsset(editingItem.id, data);
                        else await assetService.createAsset(data);
                        setShowAssetModal(false); setEditingItem(null); loadData();
                    }}
                    onCategoryCreated={(newCat) => setCategories(prev => [...prev, toCamel(newCat)])}
                />
            )}
            {showCategoryModal && (
                <CategoryFormModal isDark={isDark} category={editingItem}
                    onClose={() => { setShowCategoryModal(false); setEditingItem(null); }}
                    onSave={async (data) => {
                        if (editingItem) await assetService.updateCategory(editingItem.id, data);
                        else await assetService.createCategory(data);
                        setShowCategoryModal(false); setEditingItem(null);
                        loadCategories(); loadData();
                    }}
                />
            )}
            {showAssignModal && selectedAsset && (
                <AssignAssetModal isDark={isDark} asset={selectedAsset} employees={employees}
                    onClose={() => { setShowAssignModal(false); setSelectedAsset(null); }}
                    onAssign={async (employeeId) => {
                        await assetService.assignAsset(selectedAsset.id, employeeId);
                        setShowAssignModal(false); setSelectedAsset(null); loadData();
                    }}
                />
            )}
            {showMaintenanceModal && selectedAsset && (
                <MaintenanceModal isDark={isDark} asset={selectedAsset}
                    onClose={() => { setShowMaintenanceModal(false); setSelectedAsset(null); }}
                    onSave={async (data) => {
                        await assetService.createMaintenance(selectedAsset.id, data);
                        setShowMaintenanceModal(false); setSelectedAsset(null); loadData();
                    }}
                />
            )}
            {showDetailModal && selectedAsset && (
                <AssetDetailModal isDark={isDark} assetId={selectedAsset.id}
                    onClose={() => { setShowDetailModal(false); setSelectedAsset(null); }}
                />
            )}
        </div>
    );
};

// ─── Sub-Components ──────────────────────────────────────────────────

const LoadingSpinner = () => (
    <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
);
const EmptyState = ({ message }) => (
    <div className="text-center py-12">
        <Package className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
        <p className="text-gray-500 dark:text-gray-400">{message}</p>
    </div>
);
const ErrorCard = ({ error, onRetry, isDark }) => (
    <div className={`rounded-xl p-8 text-center border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <AlertCircle className="w-10 h-10 mx-auto mb-3 text-red-500" />
        <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Failed to load</h3>
        <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{error}</p>
        <button onClick={onRetry} className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Retry</button>
    </div>
);
const EmptyCard = ({ isDark }) => (
    <div className={`rounded-xl p-8 text-center border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <Package className="w-10 h-10 mx-auto mb-3 text-gray-400" />
        <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>No data yet</h3>
        <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Add some assets to see dashboard stats.</p>
    </div>
);
const Card = ({ title, isDark, action, children }) => (
    <div className={`rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
            {action && (
                <button onClick={action.onClick}
                    className={`text-xs flex items-center gap-1 ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}>
                    {action.label} <ChevronRight className="w-3 h-3" />
                </button>
            )}
        </div>
        {children}
    </div>
);

// ─── Asset Form Modal ────────────────────────────────────────────────
const AssetFormModal = ({ isDark, asset, categories, onClose, onSave, onCategoryCreated }) => {
    const [form, setForm] = useState({
        name: asset?.name || '', categoryId: asset?.categoryId || asset?.category_id || '',
        description: asset?.description || '', serialNumber: asset?.serialNumber || asset?.serial_number || '',
        purchaseDate: (asset?.purchaseDate || asset?.purchase_date || '')?.split('T')[0] || '',
        purchaseCost: asset?.purchaseCost || asset?.purchase_cost || '',
        condition: asset?.condition || 'new', notes: asset?.notes || '', location: asset?.location || '',
    });
    const [saving, setSaving] = useState(false);
    const [showNewCategory, setShowNewCategory] = useState(false);
    const [newCatName, setNewCatName] = useState('');
    const [creatingCat, setCreatingCat] = useState(false);

    const handleCreateCategory = async () => {
        if (!newCatName.trim()) return;
        setCreatingCat(true);
        try {
            const res = await assetService.createCategory({ name: newCatName.trim(), depreciationRate: 15, usefulLifeYears: 5 });
            if (res.success && res.data) {
                const cat = toCamel(res.data);
                setForm(f => ({ ...f, categoryId: cat.id }));
                if (onCategoryCreated) onCategoryCreated(cat);
                setShowNewCategory(false); setNewCatName('');
            }
        } catch (err) { console.error('Error creating category:', err); }
        finally { setCreatingCat(false); }
    };

    const handleSave = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        try { await onSave({ ...form, purchaseCost: form.purchaseCost ? Number(form.purchaseCost) : 0, categoryId: form.categoryId || null }); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className={`w-full max-w-lg rounded-2xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'} p-6 max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{asset ? 'Edit Asset' : 'Add Asset'}</h3>
                    <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
                </div>
                <div className="space-y-3">
                    <InputField label="Name *" value={form.name} onChange={v => setForm({...form, name: v})} isDark={isDark} />
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Category</label>
                            {!showNewCategory ? (
                                <div className="flex gap-1">
                                    <select value={form.categoryId} onChange={e => setForm({...form, categoryId: e.target.value})}
                                        className={`flex-1 px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500/20`}>
                                        <option value="">None</option>
                                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                    <button type="button" onClick={() => setShowNewCategory(true)} title="Create new category"
                                        className={`px-2 rounded-lg border text-sm font-medium ${isDark ? 'bg-blue-600/20 border-blue-500/30 text-blue-400 hover:bg-blue-600/30' : 'bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100'}`}>
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="flex gap-1">
                                    <input type="text" value={newCatName} onChange={e => setNewCatName(e.target.value)}
                                        placeholder="Category name..." autoFocus
                                        onKeyDown={e => { if (e.key === 'Enter') handleCreateCategory(); if (e.key === 'Escape') setShowNewCategory(false); }}
                                        className={`flex-1 px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-blue-500/20`} />
                                    <button type="button" onClick={handleCreateCategory} disabled={creatingCat || !newCatName.trim()}
                                        className="px-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                                        {creatingCat ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                    </button>
                                    <button type="button" onClick={() => { setShowNewCategory(false); setNewCatName(''); }}
                                        className={`px-2 rounded-lg border text-sm ${isDark ? 'border-gray-600 text-gray-400 hover:bg-gray-700' : 'border-gray-300 text-gray-500 hover:bg-gray-100'}`}>
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                        </div>
                        <InputField label="Serial Number" value={form.serialNumber} onChange={v => setForm({...form, serialNumber: v})} isDark={isDark} />
                    </div>
                    <InputField label="Description" value={form.description} onChange={v => setForm({...form, description: v})} isDark={isDark} multiline />
                    <div className="grid grid-cols-3 gap-3">
                        <InputField label="Purchase Date" type="date" value={form.purchaseDate} onChange={v => setForm({...form, purchaseDate: v})} isDark={isDark} />
                        <InputField label="Cost (₹)" type="number" value={form.purchaseCost} onChange={v => setForm({...form, purchaseCost: v})} isDark={isDark} />
                        <SelectField label="Condition" value={form.condition} onChange={v => setForm({...form, condition: v})} isDark={isDark}
                            options={['new', 'good', 'fair', 'poor'].map(c => ({ value: c, label: c.charAt(0).toUpperCase() + c.slice(1) }))} />
                    </div>
                    <InputField label="Location" value={form.location} onChange={v => setForm({...form, location: v})} isDark={isDark} />
                    <InputField label="Notes" value={form.notes} onChange={v => setForm({...form, notes: v})} isDark={isDark} multiline />
                </div>
                <div className="flex gap-3 mt-4 justify-end">
                    <button onClick={onClose} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}>Cancel</button>
                    <button onClick={handleSave} disabled={saving || !form.name.trim()}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Assign Asset Modal ──────────────────────────────────────────────
const AssignAssetModal = ({ isDark, asset, employees, onClose, onAssign }) => {
    const [search, setSearch] = useState('');
    const [selectedEmp, setSelectedEmp] = useState('');
    const [assigning, setAssigning] = useState(false);

    const filtered = employees.filter(e => {
        const name = `${e.first_name || ''} ${e.last_name || ''}`.toLowerCase();
        const code = (e.employee_code || '').toLowerCase();
        return name.includes(search.toLowerCase()) || code.includes(search.toLowerCase());
    });

    const handleAssign = async () => {
        if (!selectedEmp) return;
        setAssigning(true);
        try { await onAssign(selectedEmp); } finally { setAssigning(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className={`w-full max-w-md rounded-2xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'} p-6`} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Assign Asset</h3>
                    <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
                </div>
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Assign <span className="font-medium text-blue-600">{asset.assetCode || asset.name}</span> to an employee
                </p>
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-3 ${isDark ? 'bg-gray-700 border border-gray-600' : 'bg-gray-50 border border-gray-200'}`}>
                    <Search className="w-4 h-4 text-gray-400" />
                    <input type="text" placeholder="Search employee..." value={search} onChange={e => setSearch(e.target.value)}
                        className={`flex-1 bg-transparent text-sm outline-none ${isDark ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`} />
                </div>
                <div className={`max-h-60 overflow-y-auto rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    {filtered.length === 0 ? (
                        <p className={`text-sm text-center py-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No employees found</p>
                    ) : filtered.map(emp => (
                        <button key={emp.id} onClick={() => setSelectedEmp(emp.id)}
                            className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                                selectedEmp === emp.id
                                    ? isDark ? 'bg-blue-900/40' : 'bg-blue-50'
                                    : isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                            }`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                selectedEmp === emp.id ? 'bg-blue-600 text-white' : isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                                {(emp.first_name?.[0] || '')}{(emp.last_name?.[0] || '')}
                            </div>
                            <div>
                                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{emp.first_name} {emp.last_name}</p>
                                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{emp.employee_code} · {emp.department_name || 'No dept'}</p>
                            </div>
                        </button>
                    ))}
                </div>
                <div className="flex gap-3 mt-4 justify-end">
                    <button onClick={onClose} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}>Cancel</button>
                    <button onClick={handleAssign} disabled={assigning || !selectedEmp}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors">
                        {assigning ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Assign'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Maintenance Modal ───────────────────────────────────────────────
const MaintenanceModal = ({ isDark, asset, onClose, onSave }) => {
    const [form, setForm] = useState({ type: 'repair', description: '', scheduledDate: '', cost: '', vendor: '', notes: '' });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!form.description.trim()) return;
        setSaving(true);
        try {
            await onSave({
                ...form,
                cost: form.cost ? Number(form.cost) : 0,
                scheduledDate: form.scheduledDate || null,
            });
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className={`w-full max-w-md rounded-2xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'} p-6`} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Schedule Maintenance</h3>
                    <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
                </div>
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    For <span className="font-medium text-blue-600">{asset.assetCode || asset.name}</span>
                </p>
                <div className="space-y-3">
                    <SelectField label="Type" value={form.type} onChange={v => setForm({...form, type: v})} isDark={isDark}
                        options={['repair', 'inspection', 'upgrade', 'calibration'].map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }))} />
                    <InputField label="Description *" value={form.description} onChange={v => setForm({...form, description: v})} isDark={isDark} />
                    <div className="grid grid-cols-2 gap-3">
                        <InputField label="Scheduled Date" type="date" value={form.scheduledDate} onChange={v => setForm({...form, scheduledDate: v})} isDark={isDark} />
                        <InputField label="Cost (₹)" type="number" value={form.cost} onChange={v => setForm({...form, cost: v})} isDark={isDark} />
                    </div>
                    <InputField label="Vendor" value={form.vendor} onChange={v => setForm({...form, vendor: v})} isDark={isDark} />
                    <InputField label="Notes" value={form.notes} onChange={v => setForm({...form, notes: v})} isDark={isDark} multiline />
                </div>
                <div className="flex gap-3 mt-4 justify-end">
                    <button onClick={onClose} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}>Cancel</button>
                    <button onClick={handleSave} disabled={saving || !form.description.trim()}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Schedule'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Category Form Modal ─────────────────────────────────────────────
const CategoryFormModal = ({ isDark, category, onClose, onSave }) => {
    const [form, setForm] = useState({
        name: category?.name || '', description: category?.description || '',
        depreciationRate: category?.depreciationRate ?? category?.depreciation_rate ?? 15,
        usefulLifeYears: category?.usefulLifeYears ?? category?.useful_life_years ?? 5,
    });
    const [saving, setSaving] = useState(false);
    const handleSave = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        try { await onSave(form); } finally { setSaving(false); }
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className={`w-full max-w-md rounded-2xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'} p-6`} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{category ? 'Edit Category' : 'Add Category'}</h3>
                    <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
                </div>
                <div className="space-y-3">
                    <InputField label="Name *" value={form.name} onChange={v => setForm({...form, name: v})} isDark={isDark} />
                    <InputField label="Description" value={form.description} onChange={v => setForm({...form, description: v})} isDark={isDark} multiline />
                    <div className="grid grid-cols-2 gap-3">
                        <InputField label="Depreciation %/yr" type="number" value={form.depreciationRate} onChange={v => setForm({...form, depreciationRate: v})} isDark={isDark} />
                        <InputField label="Useful Life (yrs)" type="number" value={form.usefulLifeYears} onChange={v => setForm({...form, usefulLifeYears: v})} isDark={isDark} />
                    </div>
                </div>
                <div className="flex gap-3 mt-4 justify-end">
                    <button onClick={onClose} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}>Cancel</button>
                    <button onClick={handleSave} disabled={saving || !form.name.trim()}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Asset Detail Modal ──────────────────────────────────────────────
const AssetDetailModal = ({ isDark, assetId, onClose }) => {
    const [asset, setAsset] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await assetService.getAsset(assetId);
                if (res.success) {
                    const d = toCamel(res.data);
                    d.assignments = toCamelList(d.assignments);
                    d.maintenance = toCamelList(d.maintenance);
                    setAsset(d);
                }
            } catch (err) { console.error(err); }
            finally { setLoading(false); }
        })();
    }, [assetId]);

    if (loading) return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;
    if (!asset) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className={`w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'} p-6`} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{asset.name}</h3>
                        <p className={`text-sm ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{asset.assetCode || asset.id?.slice(0,8)}</p>
                    </div>
                    <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    {[
                        { label: 'Status', value: <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[asset.status]}`}>{asset.status}</span> },
                        { label: 'Condition', value: <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CONDITION_COLORS[asset.condition]}`}>{asset.condition}</span> },
                        { label: 'Category', value: asset.categoryName || '—' },
                        { label: 'Serial #', value: asset.serialNumber || '—' },
                        { label: 'Purchase Cost', value: `₹${Number(asset.purchaseCost || 0).toLocaleString('en-IN')}` },
                        { label: 'Current Value', value: `₹${Number(asset.currentValue || 0).toLocaleString('en-IN')}` },
                        { label: 'Purchase Date', value: asset.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString('en-IN') : '—' },
                        { label: 'Location', value: asset.location || '—' },
                    ].map((item, i) => (
                        <div key={i} className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                            <p className={`text-[10px] uppercase font-semibold ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.label}</p>
                            <p className={`text-sm mt-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.value}</p>
                        </div>
                    ))}
                </div>
                {asset.assignments?.length > 0 && (
                    <div className="mb-4">
                        <h4 className={`font-semibold text-sm mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Assignment History</h4>
                        <div className="space-y-2">
                            {asset.assignments.map((a, i) => (
                                <div key={i} className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                                    <div>
                                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{a.employeeName || a.employee_name || 'Unknown'}</p>
                                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {new Date(a.assignedAt || a.assigned_at).toLocaleDateString('en-IN')}
                                            {(a.returnedAt || a.returned_at) && ` → ${new Date(a.returnedAt || a.returned_at).toLocaleDateString('en-IN')}`}
                                        </p>
                                    </div>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-600 dark:text-gray-300'}`}>
                                        {a.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {asset.maintenance?.length > 0 && (
                    <div>
                        <h4 className={`font-semibold text-sm mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Maintenance Records</h4>
                        <div className="space-y-2">
                            {asset.maintenance.map((m, i) => (
                                <div key={i} className={`flex items-center justify-between p-2 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                                    <div>
                                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{m.description}</p>
                                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {m.type} · {(m.scheduledDate || m.scheduled_date) ? new Date(m.scheduledDate || m.scheduled_date).toLocaleDateString('en-IN') : 'No date'}
                                            {m.cost > 0 && ` · ₹${Number(m.cost).toLocaleString('en-IN')}`}
                                        </p>
                                    </div>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        m.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                        m.status === 'in_progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
                                        {m.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// ─── Shared Input Components ─────────────────────────────────────────
const InputField = ({ label, value, onChange, isDark, type = 'text', multiline }) => (
    <div>
        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{label}</label>
        {multiline ? (
            <textarea value={value} onChange={e => onChange(e.target.value)} rows={2}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500/20`} />
        ) : (
            <input type={type} value={value} onChange={e => onChange(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500/20`} />
        )}
    </div>
);

const SelectField = ({ label, value, onChange, isDark, options }) => (
    <div>
        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{label}</label>
        <select value={value} onChange={e => onChange(e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500/20`}>
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
    </div>
);

export default AssetManagement;
